import React from 'react';
import { Check, X } from 'lucide-react';
import { Badge } from '../ui/badge';
import { formatPrice } from './PlanPricingTabs';
import { BILLING_CYCLES, pricingForCycle } from '../../utils/planPricing';

const MODULE_LABELS = {
  students: 'Student Management', staff: 'Staff Management', attendance: 'Attendance',
  fees: 'Fees', payroll: 'Payroll', exams: 'Exams', library: 'Library',
  transport: 'Transport', hostel: 'Hostel', reception: 'Reception', reports: 'Reports',
  sms: 'SMS', whatsapp: 'WhatsApp', mobile_app: 'Mobile App',
};

const LIMIT_LABELS = {
  max_students: 'Max Students', max_teachers: 'Max Teachers', max_staff: 'Max Staff',
  max_parents: 'Max Parents', max_classes: 'Max Classes', max_sections: 'Max Sections',
};

const SUPPORT_LABELS = {
  custom_logo: 'Custom Logo', white_label: 'White Label', custom_domain: 'Custom Domain',
  email_support: 'Email Support', whatsapp_support: 'WhatsApp Support', priority_support: 'Priority Support',
};

function FlagRow({ label, enabled }) {
  return (
    <div className="flex items-center justify-between text-sm py-1">
      <span className="text-muted-foreground">{label}</span>
      {enabled ? <Check className="w-4 h-4 text-green-600" /> : <X className="w-4 h-4 text-muted-foreground" />}
    </div>
  );
}

/**
 * @param {object} props
 * @param {object} props.plan
 * @param {boolean} [props.compact]
 */
export default function PlanDetailView({ plan, compact = false }) {
  if (!plan) return null;

  const limits = plan.features?.limits ?? {};
  const modules = plan.features?.modules ?? {};
  const support = plan.features?.support ?? {};
  const currency = plan.currency || 'INR';

  if (compact) {
    return (
      <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2 text-sm">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold">{plan.name}</span>
          {plan.is_popular && <Badge className="bg-blue-600 text-white text-xs">Popular</Badge>}
          {plan.trial_days > 0 && (
            <Badge variant="outline" className="text-xs">{plan.trial_days}-day trial</Badge>
          )}
        </div>
        {plan.description && <p className="text-muted-foreground text-xs">{plan.description}</p>}
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          <span>Students: {limits.max_students ?? '—'}</span>
          <span>Teachers: {limits.max_teachers ?? '—'}</span>
          <span>Classes: {limits.max_classes ?? '—'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <h3 className="text-lg font-bold">{plan.name}</h3>
          {plan.is_popular && <Badge className="bg-blue-600 text-white">Popular</Badge>}
          {plan.trial_days > 0 && (
            <Badge variant="outline">{plan.trial_days}-day free trial</Badge>
          )}
        </div>
        {plan.description && <p className="text-muted-foreground text-sm">{plan.description}</p>}
      </div>

      <div>
        <h4 className="text-sm font-semibold mb-2">Pricing</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {BILLING_CYCLES.map(({ key, label }) => {
            const pricing = pricingForCycle(plan, key);
            const hasDiscount = pricing.amount_saved > 0;
            return (
              <div key={key} className="rounded-lg border border-border p-3">
                <p className="text-xs text-muted-foreground mb-1">{label}</p>
                <p className="font-bold text-lg">{formatPrice(pricing.selling_price, currency)}</p>
                {hasDiscount && (
                  <div className="mt-1 space-y-0.5 text-xs">
                    <p className="text-muted-foreground line-through">{formatPrice(pricing.default_price, currency)}</p>
                    <p className="text-green-700 dark:text-green-400">{pricing.discount_percent}% off · Save {formatPrice(pricing.amount_saved, currency)}</p>
                  </div>
                )}
                {pricing.save_extra > 0 && (
                  <p className="mt-1 text-xs font-semibold text-green-700 dark:text-green-400">
                    Save Extra {formatPrice(pricing.save_extra, currency)}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold mb-2">User Limits</h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {Object.entries(LIMIT_LABELS).map(([key, label]) => (
            <div key={key} className="text-sm">
              <span className="text-muted-foreground">{label}: </span>
              <span className="font-medium">{limits[key] ?? '—'}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold mb-2">Modules</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-0">
          {Object.entries(MODULE_LABELS).map(([key, label]) => (
            <FlagRow key={key} label={label} enabled={!!modules[key]} />
          ))}
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold mb-2">Support</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-0">
          {Object.entries(SUPPORT_LABELS).map(([key, label]) => (
            <FlagRow key={key} label={label} enabled={!!support[key]} />
          ))}
        </div>
      </div>
    </div>
  );
}
